import { Box, Tab, Tabs, Toolbar, Typography } from '@mui/material'
import { useEffect, useRef } from 'react'
import { statusTabs } from '../../../constants/prepare.jsx'
import { usePrepare } from '../../../hooks/usePrepare.js'
import { ColumnConfiguration, CommonTable, Search } from '../../common/index.js'
import KeyFilter from '../Define/common/KeyFilter.jsx'
import ActionIcons from './ActionIcon'
import StatusDot from './StatusDot'

export default function Prepare() {
  const tableContainerRef = useRef(null)
  const {
    // State
    selectedTab,
    selectedSubTab,
    selectedRows,
    editMode,
    editingData,
    expandedRows,
    loading,
    showTable,
    isFullScreen,
    includeSwitchOn,
    fileSummaryDetails,
    totalSumData,
    totalTransactions,
    // Computed values
    currentStatus,
    transactionData,
    currentColumns,
    selectedCount,
    visibleColumns,
    columnConfigOpen,
    columnConfigAnchor,
    filteredColumns,
    searchFields,
    totalItems,
    itemsPerPage,
    currentPage,
    searchCriteria,
    setSearchCriteria,
    searchOpen,
    anchorEl,
    isSuggestedRateLoading,
    isReprocessLoading,
    isCooldown,
    downloadPopperOpen,

    // Event handlers
    handleTabChange,
    handleSubTabChange,
    handleSelectRow,
    handleSelectAll,
    handleEdit,
    handleFieldChange,
    handleSaveEdit,
    handleView,
    handleRowExpand,
    handleColumnChange,
    handleColumnConfigClose,
    handleColumnConfigClick,
    handleCloseSearch,
    handleApplySearch,
    handleResetSearch,
    handleSearch,
    handlePageChange,
    handleItemsPerPageChange,
    handleFullscreen,
    handleFullScreenExit,
    handleIncludeSwitchChange,
    handleExcludeItems,
    handleSuggestTDSRate,
    handleCloseEditMode,
    handleReprocess,
    handleDownloadIconClick,
    handleDownloadPopperClose,
    handleReportDownload,
  } = usePrepare()

  const mergedTabs = statusTabs.map(tab => {
    const found = fileSummaryDetails?.find(
      item => item.uploadDataCategory === tab.screenName
    )
    return {
      ...tab,
      count: found ? found.totalRecords : 0,
    }
  })

  // Reset table scroll position when tab changes
  useEffect(() => {
    if (tableContainerRef.current) {
      const tableContainer = tableContainerRef.current.querySelector('.MuiTableContainer-root')
      if (tableContainer) {
        tableContainer.scrollLeft = 0
        tableContainer.scrollTop = 0
      }
    }
  }, [selectedTab, selectedSubTab])


  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {!isFullScreen && (
        <KeyFilter handleViewClick={handleView} isYear={true} isMonth={true} isMultiTAN={true} />
      )}

      {showTable && (
        <>
          {/* Header */}
          <Toolbar sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 500,
              }}
            >
              {`Total Transactions (${totalTransactions})`}
            </Typography>
            <ActionIcons
              isFullScreen={isFullScreen}
              editMode={editMode}
              selectedCount={selectedCount}
              includeSwitchOn={includeSwitchOn}
              selectedTabIndex={selectedTab}
              isSuggestedRateLoading={isSuggestedRateLoading}
              isReprocessLoading={isReprocessLoading}
              isCooldown={isCooldown}
              handleColumnConfigClick={handleColumnConfigClick}
              handleFullscreen={handleFullscreen}
              handleFullScreenExit={handleFullScreenExit}
              handleSaveEdit={handleSaveEdit}
              handleEdit={handleEdit}
              handleSearch={handleSearch}
              handleIncludeSwitchChange={handleIncludeSwitchChange}
              handleExcludeItems={handleExcludeItems}
              handleSuggestTDSRate={handleSuggestTDSRate}
              handleCloseEditMode={handleCloseEditMode}
              handleReprocess={handleReprocess}
              downloadPopperOpen={downloadPopperOpen}
              handleDownloadIconClick={handleDownloadIconClick}
              handleDownloadPopperClose={handleDownloadPopperClose}
              handleReportDownload={handleReportDownload}
            />
          </Toolbar>

          {/* Status Tabs */}
          <Box

          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '14px',
                  minWidth: 120,
                  padding: '12px 16px',
                  background: 'var(--bg-layer-1)',
                  borderBottom: '2px solid var(--forbrand-secondary)',
                  color: 'var(--forbrand-secondary)',
                  '&.Mui-selected': {
                    color: 'var(--forbrand-primary)',
                    fontWeight: 700,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'var(--forbrand-color)',
                  height: 4,
                },
                '.MuiTabs-list': {
                  display: 'grid',
                  gridTemplateColumns: 'auto auto auto auto auto auto',
                },
              }}
            >
              {mergedTabs.map((tab, index) => (
                <Tab
                  key={tab.label}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {`${tab.label} (${tab.count})`}
                    </Box>
                  }
                  sx={{
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: 'var(--forbrand-color)',
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {selectedTab === 4 && statusTabs[selectedTab].children && (
            <Box
              sx={{
                display: 'flex',
                borderLeft: '1px solid var(--border-subtel)',
                borderRight: '1px solid var(--border-subtel)',
              }}
            >
              {statusTabs[selectedTab].children.map((subTab, index) => (
                <Box
                  key={subTab.label}
                  onClick={() => handleSubTabChange(subTab.id)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: selectedSubTab === subTab.id ? 'var(--forbrand-primary)' : 'var(--forbrand-primary)',
                    borderBottom:
                      selectedSubTab === subTab.id ? '2px solid var(--forbrand-color)' : 'none',
                    backgroundColor:
                      selectedSubTab === subTab.id ? '#ffffff' : 'transparent',
                    fontWeight: selectedSubTab === subTab.id ? '700' : '400',

                  }}
                >
                  {subTab.label}
                </Box>
              ))}
            </Box>
          )}
          {/* Data Table */}
          <Box ref={tableContainerRef}>
            <CommonTable
              columns={filteredColumns}
              tabStatus={currentStatus} // in case of prepare section only
              data={transactionData}
              loading={loading}
              selectedItems={selectedRows}
              showCheckbox={true}
              showActions={false}
              editMode={editMode}
              editingData={editingData}
              onFieldChange={handleFieldChange}
              onDelete={row => console.log('Delete:', row)}
              showFooter={true}
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              emptyMessage={`No ${selectedTab === 4 && statusTabs[selectedTab].children
                ? statusTabs[selectedTab].children.filter((child) => child.id === selectedSubTab)[0]?.label.toLowerCase()
                : statusTabs[selectedTab].label.toLowerCase()
                } transactions found`}
              expandedRows={expandedRows}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onSelectItem={handleSelectRow}
              onSelectAll={handleSelectAll}
              onRowExpand={handleRowExpand}
              showExpandColumn={currentStatus === 'mapped'}
              totalSumData={totalSumData}
            />
          </Box>
        </>
      )}


      <ColumnConfiguration
        columns={currentColumns}
        visibleColumns={visibleColumns}
        onColumnChange={handleColumnChange}
        open={columnConfigOpen}
        onClose={handleColumnConfigClose}
        anchorEl={columnConfigAnchor}
      />

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </Box>
  )
}
